import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import { Notification, ListNotificationsResponse } from '@workspace/lib/api/notification'

const extractData = (response: InfiniteData<ListNotificationsResponse> | undefined): Notification[] => {
    if (!response) return []
    return response.pages.flatMap(page => (Array.isArray(page.data) ? page.data : []))
}

const extractUnReadCount = (response: InfiniteData<ListNotificationsResponse> | undefined): number => {
    return response?.pages?.[0]?.meta?.unReadCount ?? 0
}

export const useNotifications = () => {
    const {
        data: standardResponse,
        isLoading: loadingStandard,
        fetchNextPage: fetchNextPageStandard,
        hasNextPage: hasNextPageStandard,
        isFetchingNextPage: isFetchingNextPageStandard,
    } = useInfiniteQuery({
        queryKey: ['notifications', 'standard'],
        queryFn: ({ pageParam }) => api.notification.list({ page: pageParam, take: 10 }),
        initialPageParam: 1,
        getNextPageParam: lastPage => (lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined),
    })

    const {
        data: approvalResponse,
        isLoading: loadingApprovals,
        fetchNextPage: fetchNextPageApproval,
        hasNextPage: hasNextPageApproval,
        isFetchingNextPage: isFetchingNextPageApproval,
    } = useInfiniteQuery({
        queryKey: ['notifications', 'approvals'],
        queryFn: ({ pageParam }) => api.notification.listApprovals({ page: pageParam, take: 10 }),
        initialPageParam: 1,
        getNextPageParam: lastPage => (lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined),
    })

    const {
        data: warningResponse,
        isLoading: loadingWarnings,
        fetchNextPage: fetchNextPageWarning,
        hasNextPage: hasNextPageWarning,
        isFetchingNextPage: isFetchingNextPageWarning,
    } = useInfiniteQuery({
        queryKey: ['notifications', 'warnings'],
        queryFn: ({ pageParam }) => api.notification.listWarnings({ page: pageParam, take: 10 }),
        initialPageParam: 1,
        getNextPageParam: lastPage => (lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined),
    })

    const isLoading = loadingStandard || loadingApprovals || loadingWarnings
    const generals = extractData(standardResponse)
    const approvals = extractData(approvalResponse)
    const warnings = extractData(warningResponse)
    const unReadCounts = {
        general: extractUnReadCount(standardResponse),
        approval: extractUnReadCount(approvalResponse),
        warning: extractUnReadCount(warningResponse),
    }

    return {
        approvals,
        generals,
        warnings,
        unReadCounts,
        isLoading,
        fetchNextPageStandard,
        hasNextPageStandard,
        isFetchingNextPageStandard,
        fetchNextPageApproval,
        hasNextPageApproval,
        isFetchingNextPageApproval,
        fetchNextPageWarning,
        hasNextPageWarning,
        isFetchingNextPageWarning,
    }
}

export const useToggleReadNotification = () => {
    const queryClient = useQueryClient()

    // Helper to update a single cache optimistically
    const updateCacheOptimistically = (queryKey: string[], id: string, isRead: boolean) => {
        queryClient.setQueryData<InfiniteData<ListNotificationsResponse>>(queryKey, prev => {
            if (!prev) return prev

            let notificationFound = false
            let wasUnread = false

            for (const page of prev.pages) {
                const notification = page.data.find(n => n.id === id)
                if (notification) {
                    notificationFound = true
                    wasUnread = !notification.isRead
                    break
                }
            }

            if (!notificationFound) return prev

            const willBeRead = isRead
            let unReadCountChange = 0
            if (wasUnread && willBeRead) {
                unReadCountChange = -1
            } else if (!wasUnread && !willBeRead) {
                unReadCountChange = 1
            }

            const updatedPages = prev.pages.map(page => {
                const newPage = { ...page }
                if (page.data.some(n => n.id === id)) {
                    newPage.data = page.data.map(n => (n.id === id ? { ...n, isRead } : n))
                }
                if (unReadCountChange !== 0) {
                    newPage.meta = {
                        ...newPage.meta,
                        unReadCount: Math.max(0, newPage.meta.unReadCount + unReadCountChange),
                    }
                }
                return newPage
            })

            return {
                ...prev,
                pages: updatedPages,
            }
        })
    }

    return useMutation({
        mutationFn: ({ id, isRead }: { id: string; isRead: boolean }) => api.notification.toggleRead(id, isRead),

        meta: { invalidates: [] },

        onMutate: async ({ id, isRead }) => {
            const previousStandard = queryClient.getQueryData<InfiniteData<ListNotificationsResponse>>([
                'notifications',
                'standard',
            ])
            const previousApprovals = queryClient.getQueryData<InfiniteData<ListNotificationsResponse>>([
                'notifications',
                'approvals',
            ])
            const previousWarnings = queryClient.getQueryData<InfiniteData<ListNotificationsResponse>>([
                'notifications',
                'warnings',
            ])

            updateCacheOptimistically(['notifications', 'standard'], id, isRead)
            updateCacheOptimistically(['notifications', 'approvals'], id, isRead)
            updateCacheOptimistically(['notifications', 'warnings'], id, isRead)

            return { previousStandard, previousApprovals, previousWarnings }
        },

        onError: (_err, _variables, context) => {
            if (context?.previousStandard) {
                queryClient.setQueryData(['notifications', 'standard'], context.previousStandard)
            }
            if (context?.previousApprovals) {
                queryClient.setQueryData(['notifications', 'approvals'], context.previousApprovals)
            }
            if (context?.previousWarnings) {
                queryClient.setQueryData(['notifications', 'warnings'], context.previousWarnings)
            }
        },
    })
}

export const useMarkAllNotificationsRead = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: () => api.notification.markAllAsRead(),
        meta: { invalidates: [['notifications']] },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
    })
}
